import React from "react"
import { Box, ButtonBase, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import { AppFormGrid, AppFormSection, AppIdentityChip, AppNote, AppResultsSkeleton } from "@components"
import { identityView } from "@utils/irisProfile"
import { Cell, FieldLabel, Helper } from "./fields"


const linkSx = {
	gap: 0.6, px: 0.5, py: 0.25, borderRadius: '6px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
	color: dsColorNew.appBlue, verticalAlign: 'baseline', '&:hover': { backgroundColor: dsColorNew.hoverBgLight },
}

/** "Settings › Account" as a link to that tab; the page asks first when there are unsaved edits (CU-869fmherf D3). */
export const AccountLink = ({ onClick, children = 'Settings › Account', icon }) => <ButtonBase onClick={onClick} sx={linkSx}>
	{!!icon && <IconSeeker icon={icon} sx={{ fontSize: 11 }}/>}{children}
</ButtonBase>

/**
 * "Who you are": the read-only mirror of Settings › Account (brief v3.4; mock `iris` / `irisnone` of
 * CU-869fknfmk). It reads Account's own signals, so a save there shows here at once. No control:
 * nothing of it enters the draft, the change count or the Save.
 */
export default function WhoYouAreSection({ identity, state, error, compare, onOpenAccount, onRetry }) {
	const view = identityView(identity, compare)

	const body = () => {
		if (state === 'idle' || state === 'loading') return <AppResultsSkeleton rows={1} columns={3} height={72}/>
		// the identity could not be read: say so — "No identity set" would be false (TL condition b)
		if (state === 'error') return <AppNote severity="error">
			<b>Could not load your identity</b> — {error?.status || 'network error'}. Nothing was changed; Iris keeps using what was saved.{' '}
			<ButtonBase onClick={onRetry} sx={{ ...linkSx, fontWeight: 600, px: 0 }}>Try again</ButtonBase>
		</AppNote>
		if (!view.hasIdentity) return <AppNote severity="warning">
			<b>No identity set yet</b> — Iris does not know which brand or site is yours. Set it in <AccountLink onClick={onOpenAccount}/>; everything else on this page still saves.
		</AppNote>

		return <AppFormGrid>
			{view.kind !== 'broker' && <Cell span={4}>
				<FieldLabel>Brands</FieldLabel>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
					{view.brands.map(b => <AppIdentityChip key={b.id} id={b.id} label={b.label} primary={b.primary} readOnly/>)}
				</Box>
				<Helper>{view.brands.length > 1 ? 'The first is “you”; the others are also yours.' : 'The first is “you”.'}</Helper>
			</Cell>}
			<Cell span={view.kind === 'broker' ? 9 : 5}>
				<FieldLabel>Sites</FieldLabel>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
					{view.sites.length
						? view.sites.map(s => <AppIdentityChip key={s.id} id={s.id} kind="site" label={s.label} market={s.market} marketName={s.market_name} readOnly/>)
						: <Typography component="span" sx={{ fontSize: 13, color: dsColorNew.textSubtle }}>None — optional for a brand</Typography>}
					{view.moreSites > 0 && <Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
						and {view.moreSites} more — see <AccountLink onClick={onOpenAccount}/>
					</Typography>}
				</Box>
				<Helper>{view.kind === 'broker' ? 'Each one is “you” in its own country.' : 'Each applies to its own country.'}</Helper>
			</Cell>
			<Cell span={3}>
				<FieldLabel>Compare</FieldLabel>
				<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle, minHeight: 28, display: 'flex', alignItems: 'center' }}>
					{view.axisLabel || '—'}
				</Typography>
				<Helper>{view.axis === 'site'
					? 'Follows your kind: a broker’s site is compared against other sites.'
					: 'Follows your kind: a brand is compared against other brands.'}</Helper>
			</Cell>
		</AppFormGrid>
	}

	return <AppFormSection
		icon="user"
		title="Who you are"
		hint="Who Iris speaks for and how she compares. It comes from Settings › Account."
		action={<AccountLink icon="pen" onClick={onOpenAccount}>Change in Settings › Account</AccountLink>}
	>
		{body()}
	</AppFormSection>
}
